/**
 * catalogFromQuoteItem — criar o produto do catálogo a partir de um item do
 * orçamento ("salvar este item no meu catálogo"), num lugar só.
 *
 * ─── POR QUE ISTO EXISTE ────────────────────────────────────────────────────
 *
 * O checkbox criava o produto em CINCO lugares, cada um com a sua LISTA
 * EXPLÍCITA de colunas — e as cinco eram diferentes. O caminho de "salvar como
 * novo" e o caminho OFFLINE não levavam categoria, código interno, estoque nem
 * classificação fiscal: o usuário preenchia o card inteiro, o app dizia
 * "sucesso", e o produto nascia com metade dos campos em branco (armadilha
 * #33/#62). Aqui a lista é uma só, e as colunas de cadastro vêm do builder puro
 * `catalogProductColumnsFromQuoteItem`.
 *
 * ─── VARIAÇÕES: A LINHA DO ORÇAMENTO APONTA PARA A PRIMEIRA FILHA ───────────
 *
 * O PAI de um grupo de variações **não é vendável nem estocável** (#30): quem
 * tem preço, código de barras e saldo próprios é a filha. Um `quote_items`
 * apontando para o pai daria uma linha que a baixa de estoque ignora em
 * silêncio. Então o pai é criado como agrupador (preço = o MENOR das filhas,
 * custo `null`) e a linha do documento é ligada à PRIMEIRA variação — o mesmo
 * "primeira variação" que a conversão do catálogo já usa para levar o saldo.
 *
 * ─── VARIAÇÕES E MATERIAIS SÃO ONLINE-ONLY ──────────────────────────────────
 *
 * Mesma razão de #30c/#65: a fila offline mapeia 1 operação → 1 uuid, e não há
 * como resolver o pai de uma filha (nem o material de um kit) criada sem rede.
 * Quem chama por este módulo já está no caminho online; o caminho offline
 * enfileira só o produto simples, e a UI recusa os dois blocos sem conexão.
 */
import { supabase } from '@/integrations/supabase/client';
import {
  catalogProductColumnsFromQuoteItem,
  quoteItemProductType,
  quoteItemUnitCost,
  type QuoteItemFormLike,
} from '@/lib/quoteItems';
import { parseCostInput } from '@/lib/cost';
import {
  parentPriceFromDrafts,
  parseVariantPrice,
  validateVariantDrafts,
  type VariantDraft,
} from '@/lib/variants';
import { bundleQuantity, type BundleItemRow } from '@/lib/productBundles';
import { normalizeInternalCode } from '@/lib/productCodes';

/**
 * O que o card de item guarda enquanto o usuário monta o cadastro. Nenhum dos
 * dois é coluna de `quote_items` — são rascunhos do produto que ainda não
 * existe, e por isso viajam no item do formulário (e no rascunho autossalvo).
 */
export interface CatalogDraftFields {
  /** Variações do PRODUTO (vazio = produto simples). */
  catalogVariants?: VariantDraft[] | null;
  /** Materiais inclusos do SERVIÇO (kit). */
  catalogBundle?: BundleItemRow[] | null;
}

export type QuoteItemForCatalog = QuoteItemFormLike &
  CatalogDraftFields & {
    id?: string;
    image_url?: string | null;
    image_url_2?: string | null;
    image_url_3?: string | null;
  };

/** As variações que valem para ESTE item — só produto, só rascunho preenchido. */
export function variantDraftsForItem(item: QuoteItemForCatalog): VariantDraft[] {
  if (quoteItemProductType(item) !== 'product') return [];
  const drafts = Array.isArray(item.catalogVariants) ? item.catalogVariants : [];
  return drafts.filter((draft) => draft && String(draft.variant_name ?? '').trim().length > 0);
}

/** Os materiais que valem para ESTE item — só serviço. */
export function bundleRowsForItem(item: QuoteItemForCatalog): BundleItemRow[] {
  if (quoteItemProductType(item) !== 'service') return [];
  const rows = Array.isArray(item.catalogBundle) ? item.catalogBundle : [];
  return rows.filter((row) => row && row.child_product_id);
}

/** Estoque mínimo da variação — `0` preservado, ausência vira `null` (#61). */
function optionalMinStock(raw: unknown): number | null {
  if (raw == null || raw === '') return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** As até três fotos do item, na ordem dos slots. */
export function itemPhotoUrls(item: QuoteItemForCatalog): string[] {
  return [item.image_url, item.image_url_2, item.image_url_3].filter(
    (url): url is string => typeof url === 'string' && url.length > 0,
  );
}

export interface CreatedCatalogProduct {
  /** O produto RAIZ (o agrupador, quando há variações). */
  productId: string;
  /**
   * O que a linha do orçamento deve referenciar: a primeira variação quando há
   * variações, senão o próprio produto. Ver a nota do cabeçalho.
   */
  linkProductId: string;
}

/**
 * Resultado em UM formato (e não uma união discriminada por `ok`): este repo
 * compila com `strict: false`, e com `strictNullChecks` desligado o TypeScript
 * não estreita união por literal booleano — `outcome.error` viraria erro de tipo
 * dentro do próprio ramo de falha.
 */
export interface CreateCatalogProductOutcome {
  /** `null` quando falhou; a razão está em `error`. */
  product: CreatedCatalogProduct | null;
  /** `null` quando deu certo. */
  error: string | null;
}

/**
 * Cria o produto (e, quando houver, as variações, as fotos e os materiais) a
 * partir de um item do orçamento. Não mostra toast nem escreve na tela: quem
 * chama decide como reportar.
 */
export async function createCatalogProductFromQuoteItem(params: {
  item: QuoteItemForCatalog;
  userId: string;
  companyId: string | null;
}): Promise<CreateCatalogProductOutcome> {
  const { item, userId, companyId } = params;

  const variants = variantDraftsForItem(item);
  const hasVariants = variants.length > 0;

  if (hasVariants) {
    const check = validateVariantDrafts(variants);
    if (!check.ok) return { product: null, error: check.error! };
  }

  const columns = catalogProductColumnsFromQuoteItem(item, { hasVariants });
  const photos = itemPhotoUrls(item);
  // Preço do agrupador é o MENOR das filhas (é o que a lista do catálogo mostra
  // como "a partir de"); o custo dele é `null` de propósito — um custo no pai
  // seria capital imobilizado fantasma, contado junto com o das filhas (#61).
  const parentPrice = hasVariants
    ? parentPriceFromDrafts(variants, item.unit_price)
    : item.unit_price;

  const { data: created, error } = await supabase
    .from('products')
    .insert({
      user_id: userId,
      company_id: companyId,
      name: item.name,
      price: parentPrice,
      // `quoteItemUnitCost` preserva 0 e devolve null para ausência —
      // `Number(null) === 0` gravaria "de graça" (#61).
      cost: hasVariants ? null : quoteItemUnitCost(item),
      // Chamada INLINE, e não o spread da variável `columns` logo acima: o guard
      // de varredura procura esta expressão DENTRO do literal, e trocá-la por um
      // spread de variável desligaria a proteção (#75).
      ...catalogProductColumnsFromQuoteItem(item, { hasVariants }),
      image_url: photos[0] ?? null,
    })
    .select('id')
    .single();

  if (error || !created?.id) {
    return { product: null, error: error?.message || 'Não foi possível salvar o item no catálogo.' };
  }

  const productId = String(created.id);

  if (photos.length > 0) {
    const { error: photosError } = await supabase.from('product_photos').insert(
      photos.map((url, position) => ({
        user_id: userId,
        company_id: companyId as string,
        product_id: productId,
        image_url: url,
        position,
      })),
    );
    // Foto é acessório do cadastro: o produto já existe e o orçamento já foi
    // gravado. Falhar aqui não pode desfazer o resto — quem chama avisa.
    if (photosError) console.error('[catalogFromQuoteItem] product_photos:', photosError);
  }

  let linkProductId = productId;

  if (hasVariants) {
    const rows = variants.map((draft) => ({
      user_id: userId,
      company_id: companyId,
      parent_product_id: productId,
      variant_name: draft.variant_name.trim(),
      // A filha guarda o nome do PAI em `name` (é isso que faz renomear o pai
      // propagar); o que a distingue é `variant_name` (#30d).
      name: item.name,
      ...columns,
      // ⚠️ As linhas abaixo vêm DEPOIS do spread de propósito: `columns` foi
      // montado com `hasVariants: true`, ou seja, com os campos do AGRUPADOR
      // (código, código de barras e estoque zerados). Quem os carrega de verdade
      // é a filha, e invertê-las devolveria null a todos eles em silêncio.
      price: parseVariantPrice(draft.price) ?? 0,
      cost: parseCostInput(draft.cost).value,
      barcode: draft.barcode?.trim() || null,
      internal_code: normalizeInternalCode(draft.internal_code),
      track_stock: item.track_stock === true,
      min_stock: item.track_stock === true ? optionalMinStock(item.min_stock) : null,
      image_url: photos[0] ?? null,
    }));

    const { data: children, error: variantsError } = await supabase
      .from('products')
      .insert(rows)
      .select('id');

    if (variantsError || !children?.length) {
      // Sem as filhas o pai é um agrupador vazio: um produto sem preço próprio
      // que ninguém consegue vender. Desfaz para não deixar isso no catálogo.
      await supabase.from('products').delete().eq('id', productId);
      return {
        product: null,
        error: variantsError?.message || 'Não foi possível salvar as variações do item.',
      };
    }

    linkProductId = String(children[0].id);
  }

  const bundle = bundleRowsForItem(item);
  if (bundle.length > 0 && companyId) {
    const { error: bundleError } = await supabase.from('product_bundle_items').insert(
      bundle.map((row) => ({
        company_id: companyId,
        parent_product_id: productId,
        child_product_id: String(row.child_product_id),
        quantity: bundleQuantity(row.quantity),
      })),
    );
    // Idem às fotos: o serviço já está no catálogo, e o kit pode ser refeito
    // pela tela do catálogo sem perder nada do resto.
    if (bundleError) console.error('[catalogFromQuoteItem] product_bundle_items:', bundleError);
  }

  return { product: { productId, linkProductId }, error: null };
}
